import { useState, useEffect } from 'react';
import {
  Brain,
  Key,
  Settings,
  MessageSquare,
  BarChart3,
  Activity,
  Eye,
  EyeOff,
  Save,
  CheckCircle2,
  AlertCircle,
  Loader2,
  TrendingUp,
  DollarSign,
  Zap,
} from 'lucide-react';
import { getFirebaseFirestore } from '@/services/firebase/config';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';

const db = getFirebaseFirestore();

type TabId = 'api-keys' | 'models' | 'prompts' | 'usage' | 'status';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ElementType;
}

const TABS: Tab[] = [
  { id: 'api-keys', label: 'API Anahtarları', icon: Key },
  { id: 'models', label: 'Model Ayarları', icon: Settings },
  { id: 'prompts', label: 'Sistem Promptları', icon: MessageSquare },
  { id: 'usage', label: 'Kullanım İstatistikleri', icon: BarChart3 },
  { id: 'status', label: 'Sistem Durumu', icon: Activity },
];

// ─── API Keys Tab ─────────────────────────────────────────────────────────────
function APIKeysTab() {
  const [keys, setKeys] = useState({ openai_key: '', sendgrid_key: '' });
  const [showKeys, setShowKeys] = useState({ openai: false, sendgrid: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'ai_config', 'api_keys'));
        if (snap.exists()) {
          const data = snap.data();
          setKeys({ openai_key: data.openai_key || '', sendgrid_key: data.sendgrid_key || '' });
        }
      } catch (err) {
        console.error('API keys yüklenemedi:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'ai_config', 'api_keys'), {
        ...keys,
        updatedAt: new Date(),
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('API keys kaydedilemedi:', err);
    } finally {
      setSaving(false);
    }
  };

  const maskKey = (key: string) => {
    if (!key) return '';
    return key.slice(0, 8) + '•'.repeat(Math.max(0, key.length - 12)) + key.slice(-4);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl text-sm text-blue-300">
        API anahtarları Firestore'da şifrelenmiş olarak saklanır. Production'da Firebase Secrets kullanın.
      </div>

      {/* OpenAI */}
      <div className="bg-[#12121a] rounded-xl p-5 border border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Brain className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">OpenAI API Anahtarı</h3>
            <p className="text-xs text-gray-400">AI chat, analiz ve haftalık raporlar için</p>
          </div>
        </div>
        <div className="relative">
          <input
            type={showKeys.openai ? 'text' : 'password'}
            value={keys.openai_key}
            onChange={(e) => setKeys(prev => ({ ...prev, openai_key: e.target.value }))}
            placeholder="sk-proj-..."
            className="w-full px-4 py-3 pr-12 bg-[#0a0a12] border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-purple-500/50 font-mono text-sm"
          />
          <button
            onClick={() => setShowKeys(prev => ({ ...prev, openai: !prev.openai }))}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
          >
            {showKeys.openai ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {keys.openai_key && !showKeys.openai && (
          <p className="text-xs text-gray-500 mt-2 font-mono">{maskKey(keys.openai_key)}</p>
        )}
      </div>

      {/* SendGrid */}
      <div className="bg-[#12121a] rounded-xl p-5 border border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <Zap className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">SendGrid API Anahtarı</h3>
            <p className="text-xs text-gray-400">Haftalık rapor e-postaları için (100/gün ücretsiz)</p>
          </div>
        </div>
        <div className="relative">
          <input
            type={showKeys.sendgrid ? 'text' : 'password'}
            value={keys.sendgrid_key}
            onChange={(e) => setKeys(prev => ({ ...prev, sendgrid_key: e.target.value }))}
            placeholder="SG...."
            className="w-full px-4 py-3 pr-12 bg-[#0a0a12] border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-purple-500/50 font-mono text-sm"
          />
          <button
            onClick={() => setShowKeys(prev => ({ ...prev, sendgrid: !prev.sendgrid }))}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
          >
            {showKeys.sendgrid ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {keys.sendgrid_key && !showKeys.sendgrid && (
          <p className="text-xs text-gray-500 mt-2 font-mono">{maskKey(keys.sendgrid_key)}</p>
        )}
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-medium hover:opacity-90 disabled:opacity-50"
      >
        {saving ? (
          <><Loader2 className="w-4 h-4 animate-spin" />Kaydediliyor...</>
        ) : saved ? (
          <><CheckCircle2 className="w-4 h-4 text-green-400" />Kaydedildi!</>
        ) : (
          <><Save className="w-4 h-4" />Kaydet</>
        )}
      </button>
    </div>
  );
}

// ─── Model Settings Tab ───────────────────────────────────────────────────────
function ModelSettingsTab() {
  const [models, setModels] = useState({
    chat: 'gpt-4o-mini',
    analysis: 'gpt-4o-mini',
    weekly_report: 'gpt-4o-mini',
    temperature: 0.7,
    max_tokens: 2000,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'ai_config', 'model_defaults'));
        if (snap.exists()) setModels(prev => ({ ...prev, ...snap.data() }));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'ai_config', 'model_defaults'), { ...models, updatedAt: new Date() }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const MODEL_OPTIONS = ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'];

  if (loading) {
    return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>;
  }

  return (
    <div className="space-y-6">
      {[
        { key: 'chat' as const, label: 'AI Chat Modeli', desc: 'Kullanıcı sohbet asistanı için' },
        { key: 'analysis' as const, label: 'Analiz Modeli', desc: 'Günlük performans analizi için' },
        { key: 'weekly_report' as const, label: 'Haftalık Rapor Modeli', desc: 'Pazartesi e-posta raporu için' },
      ].map(({ key, label, desc }) => (
        <div key={key} className="bg-[#12121a] rounded-xl p-5 border border-white/10">
          <label className="block mb-2">
            <span className="text-white font-medium">{label}</span>
            <span className="text-gray-400 text-sm ml-2">— {desc}</span>
          </label>
          <select
            value={models[key]}
            onChange={(e) => setModels(prev => ({ ...prev, [key]: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0a0a12] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
          >
            {MODEL_OPTIONS.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      ))}

      {/* Temperature */}
      <div className="bg-[#12121a] rounded-xl p-5 border border-white/10">
        <label className="block mb-3">
          <span className="text-white font-medium">Temperature</span>
          <span className="text-gray-400 text-sm ml-2">— Yaratıcılık seviyesi ({models.temperature})</span>
        </label>
        <input
          type="range"
          min={0}
          max={1}
          step={0.1}
          value={models.temperature}
          onChange={(e) => setModels(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0 - Deterministik</span>
          <span>1 - Yaratıcı</span>
        </div>
      </div>

      {/* Max Tokens */}
      <div className="bg-[#12121a] rounded-xl p-5 border border-white/10">
        <label className="block mb-3">
          <span className="text-white font-medium">Max Tokens</span>
          <span className="text-gray-400 text-sm ml-2">— Maksimum yanıt uzunluğu ({models.max_tokens})</span>
        </label>
        <input
          type="range"
          min={500}
          max={4000}
          step={100}
          value={models.max_tokens}
          onChange={(e) => setModels(prev => ({ ...prev, max_tokens: parseInt(e.target.value) }))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>500</span>
          <span>4000</span>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-medium hover:opacity-90 disabled:opacity-50"
      >
        {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Kaydediliyor...</>
          : saved ? <><CheckCircle2 className="w-4 h-4 text-green-400" />Kaydedildi!</>
          : <><Save className="w-4 h-4" />Kaydet</>}
      </button>
    </div>
  );
}

// ─── System Prompts Tab ───────────────────────────────────────────────────────
function SystemPromptsTab() {
  const [prompts, setPrompts] = useState({
    chat: 'Sen BosphorusLabs\'ın e-ticaret AI asistanısın. Kullanıcının e-ticaret işletmesini analiz edip Türkçe, net ve actionable öneriler sun. Mevcut platform verilerini dikkate al.',
    analysis: 'Sen BosphorusLabs e-ticaret analiz AI\'ısın. Platform metriklerini analiz et, sorunları tespit et ve öncelikli öneriler üret. Yanıtı JSON formatında ver.',
    weekly_report: 'Sen BosphorusLabs haftalık rapor AI\'ısın. Türkçe, profesyonel ve okunabilir Markdown rapor yaz. Geçen hafta ile karşılaştırmalı analiz yap.',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'ai_config', 'system_prompts'));
        if (snap.exists()) setPrompts(prev => ({ ...prev, ...snap.data() }));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'ai_config', 'system_prompts'), { ...prompts, updatedAt: new Date() }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const VARIABLES = ['{company_name}', '{sector}', '{platform_data}', '{issues}', '{recommendations}'];

  if (loading) {
    return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 p-4 bg-white/5 rounded-xl border border-white/10">
        <span className="text-xs text-gray-400">Kullanılabilir değişkenler:</span>
        {VARIABLES.map(v => (
          <span key={v} className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded font-mono">{v}</span>
        ))}
      </div>

      {[
        { key: 'chat' as const, label: 'AI Chat Sistem Promptu', desc: 'Kullanıcı sohbet asistanı için' },
        { key: 'analysis' as const, label: 'Analiz Sistem Promptu', desc: 'Günlük otomatik analiz için' },
        { key: 'weekly_report' as const, label: 'Haftalık Rapor Promptu', desc: 'Pazartesi e-posta raporu için' },
      ].map(({ key, label, desc }) => (
        <div key={key} className="bg-[#12121a] rounded-xl p-5 border border-white/10">
          <label className="block mb-3">
            <span className="text-white font-medium">{label}</span>
            <span className="text-gray-400 text-sm ml-2">— {desc}</span>
          </label>
          <textarea
            value={prompts[key]}
            onChange={(e) => setPrompts(prev => ({ ...prev, [key]: e.target.value }))}
            rows={5}
            className="w-full px-4 py-3 bg-[#0a0a12] border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-purple-500/50 resize-y font-mono"
          />
        </div>
      ))}

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-medium hover:opacity-90 disabled:opacity-50"
      >
        {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Kaydediliyor...</>
          : saved ? <><CheckCircle2 className="w-4 h-4 text-green-400" />Kaydedildi!</>
          : <><Save className="w-4 h-4" />Kaydet</>}
      </button>
    </div>
  );
}

// ─── Usage Stats Tab ──────────────────────────────────────────────────────────
function UsageStatsTab() {
  const [usageData, setUsageData] = useState<Array<{ date: string; tokens: number; requests: number; cost: number }>>([]);
  const [userBreakdown, setUserBreakdown] = useState<Array<{ uid: string; tokens: number; requests: number; cost: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [totalStats, setTotalStats] = useState({ tokens: 0, requests: 0, cost: 0 });

  useEffect(() => {
    const load = async () => {
      try {
        // Son 7 günün verilerini topla
        const days: Array<{ date: string; tokens: number; requests: number; cost: number }> = [];
        const now = new Date();

        for (let i = 6; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(d.getDate() - i);
          const dateStr = d.toISOString().slice(0, 10);
          days.push({ date: dateStr.slice(5), tokens: 0, requests: 0, cost: 0 });
        }

        // Kullanıcı bazlı istatistikler
        const usageStatsRef = collection(db, 'ai_usage_stats');
        const userDocs = await getDocs(usageStatsRef);

        const breakdown: typeof userBreakdown = [];
        let totalTokens = 0;
        let totalRequests = 0;

        for (const userDoc of userDocs.docs) {
          const uid = userDoc.id;
          const monthStr = now.toISOString().slice(0, 7);
          const monthSnap = await getDoc(doc(db, 'ai_usage_stats', uid, 'monthly', monthStr));
          const monthData = monthSnap.data() || {};

          const tokens = (monthData.promptTokens || 0) + (monthData.completionTokens || 0);
          const requests = monthData.requestCount || 0;
          const cost = (tokens / 1000) * 0.00015; // gpt-4o-mini fiyatı yaklaşık

          if (tokens > 0) {
            breakdown.push({ uid: uid.slice(0, 8) + '...', tokens, requests, cost });
            totalTokens += tokens;
            totalRequests += requests;
          }

          // Günlük veri
          for (let i = 6; i >= 0; i--) {
            const d = new Date(now);
            d.setDate(d.getDate() - i);
            const dateStr = d.toISOString().slice(0, 10);
            const daySnap = await getDoc(doc(db, 'ai_usage_stats', uid, 'daily', dateStr));
            const dayData = daySnap.data() || {};
            const dayIdx = 6 - i;
            if (days[dayIdx]) {
              days[dayIdx].tokens += (dayData.promptTokens || 0) + (dayData.completionTokens || 0);
              days[dayIdx].requests += dayData.requestCount || 0;
              days[dayIdx].cost += ((dayData.promptTokens || 0) + (dayData.completionTokens || 0)) / 1000 * 0.00015;
            }
          }
        }

        setUsageData(days);
        setUserBreakdown(breakdown.sort((a, b) => b.tokens - a.tokens));
        setTotalStats({ tokens: totalTokens, requests: totalRequests, cost: (totalTokens / 1000) * 0.00015 });
      } catch (err) {
        console.error('Kullanım istatistikleri yüklenemedi:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Özet kartlar */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Bu Ay Token', value: totalStats.tokens.toLocaleString(), icon: Brain, color: 'purple' },
          { label: 'Toplam İstek', value: totalStats.requests.toLocaleString(), icon: TrendingUp, color: 'blue' },
          { label: 'Tahmini Maliyet', value: `$${totalStats.cost.toFixed(4)}`, icon: DollarSign, color: 'green' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className={`bg-[#12121a] rounded-xl p-4 border border-${color}-500/20`}>
            <div className={`w-8 h-8 rounded-lg bg-${color}-500/20 flex items-center justify-center mb-2`}>
              <Icon className={`w-4 h-4 text-${color}-400`} />
            </div>
            <p className="text-2xl font-bold text-white">{value}</p>
            <p className="text-xs text-gray-400">{label}</p>
          </div>
        ))}
      </div>

      {/* Basit Bar Grafik */}
      <div className="bg-[#12121a] rounded-xl p-5 border border-white/10">
        <h3 className="text-white font-medium mb-4">Son 7 Gün - Token Kullanımı</h3>
        <div className="flex items-end gap-2 h-36">
          {usageData.map((d, i) => {
            const maxTokens = Math.max(...usageData.map(x => x.tokens), 1);
            const height = Math.max((d.tokens / maxTokens) * 100, 4);
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                <div className="relative w-full flex justify-center">
                  <div
                    style={{ height: `${height}%` }}
                    className="w-full bg-purple-500/60 hover:bg-purple-500 rounded-t-sm transition-colors min-h-[4px]"
                    title={`${d.date}: ${d.tokens.toLocaleString()} token`}
                  />
                </div>
                <span className="text-xs text-gray-500">{d.date}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Kullanıcı tablosu */}
      {userBreakdown.length > 0 && (
        <div className="bg-[#12121a] rounded-xl border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h3 className="text-white font-medium">Bu Ay Kullanıcı Bazlı Kullanım</h3>
          </div>
          <div className="divide-y divide-white/5">
            <div className="grid grid-cols-4 px-4 py-2 text-xs text-gray-400 uppercase tracking-wider">
              <span>Kullanıcı ID</span>
              <span className="text-right">Token</span>
              <span className="text-right">İstek</span>
              <span className="text-right">Maliyet</span>
            </div>
            {userBreakdown.map((u, i) => (
              <div key={i} className="grid grid-cols-4 px-4 py-3 text-sm">
                <span className="text-gray-300 font-mono">{u.uid}</span>
                <span className="text-right text-white">{u.tokens.toLocaleString()}</span>
                <span className="text-right text-gray-300">{u.requests}</span>
                <span className="text-right text-green-400">${u.cost.toFixed(4)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {userBreakdown.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <Brain className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p>Henüz AI kullanım verisi yok.</p>
          <p className="text-sm mt-1">OpenAI API anahtarını kaydedin ve AI chat'i test edin.</p>
        </div>
      )}
    </div>
  );
}

// ─── System Status Tab ────────────────────────────────────────────────────────
function SystemStatusTab() {
  const [status, setStatus] = useState({
    openAIConfigured: false,
    sendgridConfigured: false,
    lastSyncTime: null as string | null,
    scheduledJobs: [
      { name: 'syncPlatformData', schedule: 'Her saat', status: 'active' },
      { name: 'analyzePerformanceAI', schedule: 'Her gün 02:00 UTC', status: 'active' },
      { name: 'generateWeeklyReport', schedule: 'Pazartesi 05:00 UTC', status: 'active' },
    ],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const keysSnap = await getDoc(doc(db, 'ai_config', 'api_keys'));
        const keysData = keysSnap.data() || {};
        setStatus(prev => ({
          ...prev,
          openAIConfigured: !!keysData.openai_key,
          sendgridConfigured: !!keysData.sendgrid_key,
        }));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Servis Durumu */}
      <div className="bg-[#12121a] rounded-xl p-5 border border-white/10">
        <h3 className="text-white font-medium mb-4">Servis Durumu</h3>
        <div className="space-y-3">
          {[
            { label: 'OpenAI API', configured: status.openAIConfigured, desc: 'AI chat, analiz ve raporlar' },
            { label: 'SendGrid', configured: status.sendgridConfigured, desc: 'Haftalık e-posta raporları' },
          ].map(({ label, configured, desc }) => (
            <div key={label} className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-medium">{label}</p>
                <p className="text-gray-400 text-xs">{desc}</p>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                configured
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {configured ? (
                  <><CheckCircle2 className="w-3 h-3" />Yapılandırıldı</>
                ) : (
                  <><AlertCircle className="w-3 h-3" />Yapılandırılmadı</>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scheduled Jobs */}
      <div className="bg-[#12121a] rounded-xl p-5 border border-white/10">
        <h3 className="text-white font-medium mb-4">Zamanlanmış Görevler</h3>
        <div className="space-y-3">
          {status.scheduledJobs.map((job) => (
            <div key={job.name} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
              <div>
                <p className="text-white text-sm font-mono">{job.name}</p>
                <p className="text-gray-400 text-xs mt-0.5">{job.schedule}</p>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 text-green-400 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                Aktif
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bilgi kutusu */}
      <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
        <p className="text-purple-300 text-sm font-medium mb-1">Firebase Blaze Planı Gerekli</p>
        <p className="text-gray-400 text-xs">
          Scheduled Functions (zamanlanmış görevler) için Firebase Blaze (pay-as-you-go) planı gereklidir.
          Cloud Firestore ve Firebase Auth ücretsiz kotada çalışmaya devam eder.
        </p>
      </div>
    </div>
  );
}

// ─── Ana Bileşen ──────────────────────────────────────────────────────────────
export function AdminAIManagement() {
  const [activeTab, setActiveTab] = useState<TabId>('api-keys');

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-text-primary">AI Yönetimi</h1>
          <p className="text-text-secondary text-sm">OpenAI entegrasyonu, model ayarları ve kullanım istatistikleri</p>
        </div>
      </div>

      {/* Tab Navigasyonu */}
      <div className="flex items-center gap-1 bg-bg-card rounded-xl p-1 border border-border-default overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-accent text-white'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab İçeriği */}
      <div className="bg-bg-card rounded-2xl p-6 border border-border-default">
        {activeTab === 'api-keys' && <APIKeysTab />}
        {activeTab === 'models' && <ModelSettingsTab />}
        {activeTab === 'prompts' && <SystemPromptsTab />}
        {activeTab === 'usage' && <UsageStatsTab />}
        {activeTab === 'status' && <SystemStatusTab />}
      </div>
    </div>
  );
}

export default AdminAIManagement;
